import { useState } from 'react';
import { Check, Copy, Heart, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DRAFT_DAYS, REFERRAL_AMOUNT, enlaceReferido, type ExitModalKind } from '@/lib/exitOffer';

interface Props {
  kind: ExitModalKind;
  onResume: () => void;
  onClose: () => void;
  onShare?: (canal: 'whatsapp' | 'copiar') => void;
}

// Ventana de salida del configurador (ver src/lib/exitOffer.ts).
export function ExitIntentModal({ kind, onResume, onClose, onShare }: Props) {
  const { t } = useTranslation();
  const [copiado, setCopiado] = useState(false);
  const enlace = enlaceReferido();
  const esReferido = kind === 'referral';

  const copiar = async () => {
    try { await navigator.clipboard.writeText(enlace); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* */ }
    onShare?.('copiar');
  };
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(t('home.referralShareText', { link: enlace }))}`;

  return (
    <div className="fixed inset-0 z-[85] bg-black/50 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="exit-modal-title">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border-2 border-[#D4AF37]/40 overflow-hidden" onClick={e => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center text-gray-400" aria-label={t('common.close')}>
          <X className="w-4 h-4" />
        </button>
        <div className="bg-gradient-to-br from-[#FAF8F5] to-[#FEF3E2] px-6 pt-7 pb-5 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#D4AF37]/15 flex items-center justify-center mb-3">
            {esReferido ? <Users className="w-6 h-6 text-[#866A12]" /> : <Heart className="w-6 h-6 text-[#866A12]" />}
          </div>
          <h2 id="exit-modal-title" className="text-2xl font-semibold text-[#1E3A5F]">
            {esReferido ? t('home.referralTitle') : t('home.exitByeTitle')}
          </h2>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm text-[#374151] leading-relaxed">
          {esReferido ? (
            <>
              <p>{t('home.referralText', { amount: REFERRAL_AMOUNT })}</p>
              <div className="flex items-center gap-2 rounded-lg border border-[#D4AF37]/40 bg-[#FAF8F5] px-3 py-2">
                <span className="flex-1 min-w-0 truncate text-[#1E3A5F] font-medium text-xs">{enlace.replace('https://', '')}</span>
                <button type="button" onClick={copiar} className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#866A12] hover:text-[#1E3A5F]">
                  {copiado ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiado ? t('home.referralCopied') : t('home.referralCopy')}
                </button>
              </div>
              <p className="text-[#6B7280] text-xs">{t('home.referralFine', { amount: REFERRAL_AMOUNT })}</p>
            </>
          ) : (
            <>
              <p>{t('home.exitByeText', { days: DRAFT_DAYS })}</p>
              <p className="text-[#6B7280]">{t('home.exitByeTip')}</p>
            </>
          )}
        </div>
        <div className="px-6 pb-6 flex flex-col gap-2">
          {esReferido ? (
            <a
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onShare?.('whatsapp')}
              className="w-full rounded-full bg-[#25D366] text-white font-medium py-3 text-center hover:bg-[#1EBE5A]"
            >
              {t('home.referralWhatsapp')}
            </a>
          ) : null}
          <button type="button" onClick={onResume} className={`w-full rounded-full font-medium py-3 ${esReferido ? 'border border-[#1E3A5F]/30 text-[#1E3A5F] hover:bg-[#1E3A5F]/5' : 'bg-[#1E3A5F] text-white hover:bg-[#162c48]'}`}>
            {t('home.exitResume')}
          </button>
          <button type="button" onClick={onClose} className="w-full rounded-full text-[#6B7280] text-sm py-2 hover:text-[#1E3A5F]">
            {t('home.exitLater')}
          </button>
        </div>
      </div>
    </div>
  );
}
